import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ArcCarousel } from '@/components/projects/arc-carousel'
import { projects } from '@/constants/projects'
import { studioMeta } from '@/constants/site'

const ProjectsPage: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        if (prefersReducedMotion()) {
            return
        }

        gsap.from('[data-head-item]', {
            yPercent: -135,
            opacity: 0,
            duration: 0.95,
            stagger: 0.09,
            delay: 0.35,
            ease: 'noma'
        })
    }, scope, [])

    return (
        <Layout theme='light' footer={false}>
            <Seo
                title='Projects'
                description='Six selected projects from the Noma Studio archive: brand identity, art direction and web design built for founders who care how they are seen.'
                ogImage={projects[0].image}
            />

            <section
                id='projects-index'
                ref={scope}
                className='relative flex h-[100dvh] flex-col overflow-hidden bg-paper text-ink'
            >
                <header
                    id='projects-head'
                    className='shrink-0 px-5 pt-[var(--nav-h)] lg:px-9'
                >
                    <div className='flex items-start justify-between gap-6 pt-3 lg:pt-4'>
                        <h1 className='flex items-start gap-1 overflow-hidden pb-[0.08em]'>
                            <span
                                data-head-item
                                className='t-cond block text-[clamp(19px,1.6vw,23px)] leading-none'
                            >
                                selected works
                            </span>

                            <span
                                data-head-item
                                className='t-mono mt-[1px] block text-brand'
                            >
                                ({projects.length})
                            </span>
                        </h1>

                        <p className='overflow-hidden pb-[0.08em]'>
                            <span
                                data-head-item
                                className='t-cond block text-[clamp(19px,1.6vw,23px)] leading-none'
                            >
                                ({studioMeta.years})
                            </span>
                        </p>
                    </div>
                </header>

                <ArcCarousel projects={projects} className='min-h-0 flex-1' />

                {/* Plain list of every project so crawlers and screen readers get
                    the whole archive without driving the carousel. */}
                <nav aria-label='Project links' className='sr-only'>
                    <ul>
                        {projects.map((project) => (
                            <li key={project.slug}>
                                <a href={`/projects/${project.slug}`}>
                                    {project.title}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            </section>
        </Layout>
    )
}

export default ProjectsPage
